import { firebaseAuth } from '@/config/firebase'
import { getLocale, getTimezone } from '@/config/locale'

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

interface RequestOptions {
  method?: HttpMethod
  body?: unknown
  signal?: AbortSignal
  headers?: Record<string, string>
}

type QueryValue = string | number | boolean | null | undefined

const API_BASE_URL = 'https://golero.nl'
const API_PREFIX = '/api'

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

function normalizeApiPath(path: string) {
  const trimmedPath = path.trim()
  if (!trimmedPath) {
    throw new Error('[api] Empty backend path received.')
  }

  if (/^https?:\/\//i.test(trimmedPath)) {
    const absoluteUrl = new URL(trimmedPath)
    if (absoluteUrl.origin !== API_BASE_URL) {
      throw new Error(
        `[api] Absolute backend URL must target ${API_BASE_URL}, received ${absoluteUrl.origin}.`,
      )
    }

    if (
      absoluteUrl.pathname !== API_PREFIX &&
      !absoluteUrl.pathname.startsWith(`${API_PREFIX}/`)
    ) {
      throw new Error(
        `[api] Backend URL must use the ${API_PREFIX} prefix, received ${absoluteUrl.pathname}.`,
      )
    }

    return `${absoluteUrl.pathname}${absoluteUrl.search}${absoluteUrl.hash}`
  }

  const normalizedPath = trimmedPath.startsWith('/') ? trimmedPath : `/${trimmedPath}`
  if (
    normalizedPath === API_PREFIX ||
    normalizedPath.startsWith(`${API_PREFIX}/`)
  ) {
    return normalizedPath
  }

  // The mobile app always targets /api routes on golero.nl, so the SPA must do the same.
  return `${API_PREFIX}${normalizedPath}`
}

export function buildApiUrl(
  path: string,
  query?: Record<string, QueryValue>,
) {
  const normalizedPath = normalizeApiPath(path)
  const url = new URL(`${API_BASE_URL}${normalizedPath}`)

  // Keep the web client on the same localized API contract as Flutter.
  // The app sends `lang` on every API URL in addition to X-Locale.
  url.searchParams.set('lang', getLocale())

  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === '') continue
      url.searchParams.set(key, String(value))
    }
  }

  return url.toString()
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json; charset=UTF-8',
    'X-Timezone': getTimezone(),
    'X-App-Locale': getLocale(),
    'X-Locale': getLocale(),
    'X-Platform': 'web',
  }

  const user = firebaseAuth.currentUser
  if (!user) return headers

  const token = await user.getIdToken()
  headers.Authorization = `Bearer ${token}`
  return headers
}

function logDevelopmentRequest(
  path: string,
  url: string,
  headers: Record<string, string>,
) {
  if (!import.meta.env.DEV) return

  const normalizedPath = normalizeApiPath(path)
  if (
    normalizedPath.startsWith('/api/users') ||
    normalizedPath.startsWith('/api/users/') ||
    /\/api\/poules\/\d+\/activities(?:\?.*)?$/.test(normalizedPath)
  ) {
    console.info('[api] Request', {
      url,
      headers: {
        Accept: headers.Accept,
        'X-Timezone': headers['X-Timezone'],
        Authorization: headers.Authorization ? 'Bearer <redacted>' : undefined,
      },
    })
  }
}

export async function apiRequest<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = 'GET', body, signal, headers: extraHeaders } = options
  const requestUrl = buildApiUrl(path)
  const headers = {
    ...(await getAuthHeaders()),
    ...(extraHeaders ?? {}),
  }

  const isFormDataBody = typeof FormData !== 'undefined' && body instanceof FormData
  if (isFormDataBody) {
    // Let the browser add the multipart boundary itself.
    delete headers['Content-Type']
  }

  const init: RequestInit = {
    method,
    headers,
    body: body === undefined ? null : isFormDataBody ? body : JSON.stringify(body),
  }

  if (signal) init.signal = signal

  logDevelopmentRequest(path, requestUrl, headers)

  const response = await fetch(requestUrl, init)

  if (!response.ok) {
    const message = await response
      .json()
      .then((d: unknown) => {
        if (
          d !== null &&
          typeof d === 'object' &&
          'message' in d &&
          typeof (d as Record<string, unknown>).message === 'string'
        ) {
          return (d as { message: string }).message
        }
        return response.statusText
      })
      .catch(() => response.statusText)

    throw new ApiError(response.status, message)
  }

  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

export const api = {
  get: <T>(path: string, options?: { signal?: AbortSignal; headers?: Record<string, string> }) =>
    apiRequest<T>(path, {
      method: 'GET',
      ...(options?.signal ? { signal: options.signal } : {}),
      ...(options?.headers ? { headers: options.headers } : {}),
    }),

  post: <T>(path: string, body: unknown) =>
    apiRequest<T>(path, { method: 'POST', body }),

  put: <T>(path: string, body: unknown) =>
    apiRequest<T>(path, { method: 'PUT', body }),

  patch: <T>(path: string, body: unknown) =>
    apiRequest<T>(path, { method: 'PATCH', body }),

  delete: <T>(path: string) => apiRequest<T>(path, { method: 'DELETE' }),
  download: async (path: string): Promise<Blob> => {
    const requestUrl = buildApiUrl(path)
    const headers = await getAuthHeaders()
    const response = await fetch(requestUrl, { method: 'GET', headers })

    if (!response.ok) {
      const message = await response
        .json()
        .then((d: unknown) => (
          d !== null && typeof d === 'object' && 'message' in d && typeof (d as Record<string, unknown>).message === 'string'
            ? (d as { message: string }).message
            : response.statusText
        ))
        .catch(() => response.statusText)
      throw new ApiError(response.status, message)
    }

    return response.blob()
  },
}
